import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type CardPadding = 'none' | 'compact' | 'default' | 'spacious';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /**
   * 'compact' (12px) · 'default' (16px) · 'spacious' (24px) · 'none'.
   * Legacy boolean still works: true → 'default', false → 'none'.
   */
  padding?: CardPadding | boolean;
  /** 'flat' (default): border, NO shadow. 'raised' only when the card floats. */
  elevation?: 'flat' | 'raised';
  /** Opts into the one legal hover treatment for clickable cards. */
  interactive?: boolean;
}

const PAD: Record<CardPadding, string> = {
  none: '',
  compact: 'p-3',
  default: 'p-4',
  spacious: 'p-6',
};

function normalizePadding(padding: CardPadding | boolean): CardPadding {
  if (padding === true) return 'default';
  if (padding === false) return 'none';
  return padding;
}

export default function Card({
  children,
  padding = 'default',
  elevation = 'flat',
  interactive = false,
  className = '',
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        'bg-surface-card border border-border rounded-[var(--radius-surface)]',
        PAD[normalizePadding(padding)],
        elevation === 'raised' && 'shadow-[var(--shadow-raised)]',
        interactive &&
          'cursor-pointer transition-[box-shadow,border-color,transform] duration-[var(--dur-fast)] ease-[var(--ease-standard)] hover:shadow-[var(--shadow-raised)] hover:border-green-200 hover:-translate-y-px',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  children?: ReactNode;
  /** Structured API. When provided, renders the title/actions row. */
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function CardHeader({ children, title, subtitle, actions, className = '' }: CardHeaderProps) {
  if (title !== undefined || subtitle !== undefined || actions !== undefined) {
    return (
      <div className={cn('mb-4 flex items-start justify-between gap-3', className)}>
        <div className="min-w-0">
          {title !== undefined && <h3 className="t-card text-green-900">{title}</h3>}
          {subtitle !== undefined && <p className="mt-1 text-sm text-ink-faint">{subtitle}</p>}
          {children}
        </div>
        {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    );
  }
  return <div className={cn('mb-4', className)}>{children}</div>;
}

export function CardTitle({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <h3 className={cn('t-card text-green-900', className)}>{children}</h3>;
}

export function CardBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={cn('space-y-3', className)}>{children}</div>;
}

export function CardFooter({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('mt-4 flex items-center justify-end gap-2 border-t border-border-subtle pt-4', className)}>
      {children}
    </div>
  );
}

/** Replaces the `<Card className="text-center py-12">` empty-state overrides. */
export function CardEmpty({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <Card padding="none" className={className}>
      <div className="py-12">{children}</div>
    </Card>
  );
}
